/**
 * Gestione lavoro offline.
 *
 *  - salvaBozza / leggiBozza : conservano lo stato dell'intervento sul dispositivo,
 *    così nulla va perso se cade la rete, il tablet si spegne o il browser si chiude.
 *
 *  - postSicuro : invia al server; se la rete non c'è, mette l'operazione in coda
 *    e la rispedisce da sola appena la connessione torna.
 *
 * Le operazioni in coda inviano sempre lo stato completo (checklist, materiali...),
 * quindi un rinvio successivo sovrascrive senza creare doppioni.
 */
import api from './api';

const CHIAVE_BOZZA = 'quindi_bozza_';
const CHIAVE_CODA  = 'quindi_coda_operazioni';

/* ─────────────  BOZZE LOCALI  ───────────── */

export function salvaBozza(interventoId, dati) {
  try {
    localStorage.setItem(CHIAVE_BOZZA + interventoId, JSON.stringify({
      dati,
      aggiornata: new Date().toISOString(),
    }));
    return true;
  } catch (e) {
    console.warn('Bozza non salvata:', e);
    return false;
  }
}

export function leggiBozza(interventoId) {
  try {
    const raw = localStorage.getItem(CHIAVE_BOZZA + interventoId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function rimuoviBozza(interventoId) {
  try { localStorage.removeItem(CHIAVE_BOZZA + interventoId); } catch {}
}

/* ─────────────  CODA OPERAZIONI  ───────────── */

function leggiCoda() {
  try {
    const raw = localStorage.getItem(CHIAVE_CODA);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function scriviCoda(coda) {
  try { localStorage.setItem(CHIAVE_CODA, JSON.stringify(coda)); } catch {}
}

export function operazioniInAttesa() {
  return leggiCoda().length;
}

/** Aggiunge un'operazione alla coda, sostituendo quella precedente sullo stesso endpoint. */
function accoda(metodo, url, body) {
  const coda = leggiCoda().filter(op => !(op.url === url && op.metodo === metodo));
  coda.push({ metodo, url, body, creata: new Date().toISOString() });
  scriviCoda(coda);
}

/**
 * Invia al server. Se fallisce per mancanza di rete accoda e restituisce
 * { inviato:false, accodato:true }. Gli errori veri del server vengono rilanciati.
 */
async function inviaSicuro(metodo, url, body) {
  if (!navigator.onLine) {
    accoda(metodo, url, body);
    return { inviato: false, accodato: true };
  }
  try {
    const res = metodo === 'put' ? await api.put(url, body) : await api.post(url, body);
    return { inviato: true, accodato: false, res };
  } catch (e) {
    const senzaRisposta = !e.response;            // rete assente o server irraggiungibile
    const serverGiu = e.response && e.response.status >= 500;
    if (senzaRisposta || serverGiu) {
      accoda(metodo, url, body);
      return { inviato: false, accodato: true };
    }
    throw e;                                       // 4xx: errore applicativo, va mostrato
  }
}

export const postSicuro = (url, body) => inviaSicuro('post', url, body);
export const putSicuro  = (url, body) => inviaSicuro('put',  url, body);

/** Prova a svuotare la coda. Ritorna quante operazioni sono state inviate. */
export async function sincronizza() {
  if (!navigator.onLine) return 0;
  let coda = leggiCoda();
  if (coda.length === 0) return 0;

  let inviate = 0;
  const rimaste = [];

  for (const op of coda) {
    try {
      if (op.metodo === 'put') await api.put(op.url, op.body);
      else                     await api.post(op.url, op.body);
      inviate++;
    } catch (e) {
      if (e.response && e.response.status >= 400 && e.response.status < 500) {
        // Richiesta rifiutata dal server: inutile ritentare all'infinito
        console.warn('Operazione scartata:', op.url, e.response.status);
      } else {
        rimaste.push(op);
      }
    }
  }

  scriviCoda(rimaste);
  return inviate;
}

/**
 * Avvia la sincronizzazione automatica: al ritorno della connessione
 * e comunque ogni 30 secondi. Restituisce la funzione per fermarla.
 */
export function avviaSincronizzazione(onCambio) {
  const esegui = async () => {
    const n = await sincronizza();
    if (onCambio) onCambio({ online: navigator.onLine, inAttesa: operazioniInAttesa(), inviate: n });
  };

  const suOnline  = () => esegui();
  const suOffline = () => onCambio && onCambio({ online: false, inAttesa: operazioniInAttesa(), inviate: 0 });

  window.addEventListener('online', suOnline);
  window.addEventListener('offline', suOffline);
  const timer = setInterval(esegui, 30000);
  esegui();

  return () => {
    window.removeEventListener('online', suOnline);
    window.removeEventListener('offline', suOffline);
    clearInterval(timer);
  };
}
